import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Grid, TextField, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Alert, Button, FormControl, InputLabel, Select, MenuItem, Chip, TableContainer, Paper, TablePagination, IconButton, Tooltip } from '@mui/material';
import { Assessment, Refresh, Download } from '@mui/icons-material';
import api from '../services/api';

const endpoints = [
  '/reports/analytics/items',
  '/reports/analytics/customers',
  '/reports/analytics/categories',
  '/reports/analytics/payment-modes'
];

export default function ReportsAnalytics() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ startDate: '', endDate: '', category: '', customer: '', item: '', paymentMode: '' });
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    // default to current month
    const currentDate = new Date();
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    setFilters({
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0],
    });
  }, []);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters.startDate, filters.endDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(endpoints[tab], { params: { startDate: filters.startDate, endDate: filters.endDate } });
      setData(response.data.data || []);
      setPage(0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering for additional filters
  const filteredData = useMemo(() => {
    let rows = data;
    if (tab === 0 && filters.item) {
      const q = filters.item.toLowerCase();
      rows = rows.filter(r => (r.item_name || '').toLowerCase().includes(q) || (r.hsn_code || '').toLowerCase().includes(q));
    }
    if (tab === 1 && (filters.customer || filters.category)) {
      if (filters.customer) {
        const q = filters.customer.toLowerCase();
        rows = rows.filter(r => ((r['customer.name'] || r.customer_name || '')).toLowerCase().includes(q));
      }
    }
    if (tab === 2 && filters.category) {
      rows = rows.filter(r => (r.category || '').toLowerCase() === filters.category.toLowerCase());
    }
    if (tab === 3 && filters.paymentMode) {
      const q = filters.paymentMode.toLowerCase();
      rows = rows.filter(r => ((r['payment_mode.name'] || r.payment_mode_code || '')).toLowerCase().includes(q));
    }
    return rows;
  }, [data, tab, filters]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const exportCSV = () => {
    const headersMap = [
      ['Item,HSN,Total Weight,Lines,Total Amount'],
      ['Customer,Phone,Invoices,Total Sales'],
      ['Category,Invoices,Total Sales'],
      ['Payment Mode,Payments,Total Amount']
    ];
    const header = headersMap[tab][0];
    const rows = filteredData.map(r => {
      if (tab === 0) return [r.item_name, r.hsn_code, Number(r.total_weight||0).toFixed(3), r.line_count, Number(r.total_amount||0).toFixed(2)].join(',');
      if (tab === 1) return [r['customer.name'] || r.customer_name, r['customer.phone'] || r.customer_phone, r.invoice_count, Number(r.total_sales||0).toFixed(2)].join(',');
      if (tab === 2) return [r.category, r.invoice_count, Number(r.total_sales||0).toFixed(2)].join(',');
      return [r['payment_mode.name'] || r.payment_mode_code, r.payment_count, Number(r.total_amount||0).toFixed(2)].join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'analytics.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Analytics Reports</Typography>
        <Button variant="outlined" startIcon={<Assessment />} onClick={loadData} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {tab === 0 && (
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Search Item/HSN" value={filters.item} onChange={(e)=>setFilters({ ...filters, item: e.target.value })} />
              </Grid>
            )}
            {tab === 1 && (
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Search Customer" value={filters.customer} onChange={(e)=>setFilters({ ...filters, customer: e.target.value })} />
              </Grid>
            )}
            {tab === 2 && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select value={filters.category} label="Category" onChange={(e)=>setFilters({ ...filters, category: e.target.value })}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="GOLD">Gold</MenuItem>
                    <MenuItem value="SILVER">Silver</MenuItem>
                    <MenuItem value="DIAMOND">Diamond</MenuItem>
                    <MenuItem value="GENERAL">General</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {tab === 3 && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Payment Mode</InputLabel>
                  <Select value={filters.paymentMode} label="Payment Mode" onChange={(e)=>setFilters({ ...filters, paymentMode: e.target.value })}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="CARD">Card</MenuItem>
                    <MenuItem value="NET_BANKING">Net Banking</MenuItem>
                    <MenuItem value="CHEQUE">Cheque</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={3} display="flex" gap={1} justifyContent="flex-end">
              <Tooltip title="Reload">
                <span>
                  <IconButton onClick={loadData} disabled={loading}>
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Export CSV">
                <IconButton onClick={exportCSV}>
                  <Download />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Item-wise Sales" />
        <Tab label="Customer-wise Sales" />
        <Tab label="Category-wise Sales" />
        <Tab label="Payment Mode-wise Sales" />
      </Tabs>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
            <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                {tab === 0 && (
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>HSN</TableCell>
                    <TableCell align="right">Total Weight</TableCell>
                    <TableCell align="right">Lines</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                  </TableRow>
                )}
                {tab === 1 && (
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell align="right">Invoices</TableCell>
                    <TableCell align="right">Total Sales</TableCell>
                  </TableRow>
                )}
                {tab === 2 && (
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Invoices</TableCell>
                    <TableCell align="right">Total Sales</TableCell>
                  </TableRow>
                )}
                {tab === 3 && (
                  <TableRow>
                    <TableCell>Payment Mode</TableCell>
                    <TableCell align="right">Payments</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {paginatedData.map((row, idx) => (
                  <TableRow key={idx}>
                    {tab === 0 && (
                      <>
                        <TableCell>{row.item_name}</TableCell>
                        <TableCell>{row.hsn_code}</TableCell>
                        <TableCell align="right">{parseFloat(row.total_weight || 0).toFixed(3)}</TableCell>
                        <TableCell align="right">{row.line_count}</TableCell>
                        <TableCell align="right">{parseFloat(row.total_amount || 0).toFixed(2)}</TableCell>
                      </>
                    )}
                    {tab === 1 && (
                      <>
                        <TableCell>{row['customer.name'] || row.customer_name}</TableCell>
                        <TableCell>{row['customer.phone'] || row.customer_phone}</TableCell>
                        <TableCell align="right">{row.invoice_count}</TableCell>
                        <TableCell align="right">{parseFloat(row.total_sales || 0).toFixed(2)}</TableCell>
                      </>
                    )}
                    {tab === 2 && (
                      <>
                        <TableCell>{row.category || 'N/A'}</TableCell>
                        <TableCell align="right">{row.invoice_count}</TableCell>
                        <TableCell align="right">{parseFloat(row.total_sales || 0).toFixed(2)}</TableCell>
                      </>
                    )}
                    {tab === 3 && (
                      <>
                        <TableCell>{row['payment_mode.name'] || row.payment_mode_code}</TableCell>
                        <TableCell align="right">{row.payment_count}</TableCell>
                        <TableCell align="right">{parseFloat(row.total_amount || 0).toFixed(2)}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
                {filteredData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No data</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}


